import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMobileTheme } from '../../hooks/useMobileTheme';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const { theme } = useMobileTheme();

  // 手機版 Footer 各主題設定
  const mobileFooterTheme = (() => {
    switch (theme) {
      case 'pink':
        return {
          footerBg: 'bg-gradient-to-br from-rose-300 to-pink-400',
          textColor: 'text-white',
          mutedText: 'text-white/70',
          border: 'border-white/30',
          inputBg: 'bg-white/20 border-white/40',
          submitBtn: 'bg-white text-rose-500 hover:bg-white/90',
          menuBtn: 'bg-white/20 hover:bg-white/30',
          sectionTitle: 'text-white/60',
        };
      case 'white':
        return {
          footerBg: 'bg-gradient-to-br from-gray-100 to-gray-200',
          textColor: 'text-gray-800',
          mutedText: 'text-gray-500',
          border: 'border-gray-300',
          inputBg: 'bg-white border-gray-300',
          submitBtn: 'bg-gray-800 text-white hover:bg-gray-700',
          menuBtn: 'bg-gray-800/10 hover:bg-gray-800/20',
          sectionTitle: 'text-gray-400',
        };
      default: // tech
        return {
          footerBg: 'bg-gradient-to-br from-teal-600 to-teal-700',
          textColor: 'text-white',
          mutedText: 'text-white/70',
          border: 'border-white/20',
          inputBg: 'bg-white/10 border-white/30',
          submitBtn: 'bg-white text-teal-600 hover:bg-white/90',
          menuBtn: 'bg-white/15 hover:bg-white/25',
          sectionTitle: 'text-white/60',
        };
    }
  })();

  // 桌機版 Footer 主題設定
  const desktopFooterTheme = (() => {
    switch (theme) {
      case 'pink':
        return {
          footerBg: 'bg-gradient-to-br from-rose-100 to-pink-100',
          textColor: 'text-gray-800',
          mutedText: 'text-gray-600',
          border: 'border-rose-200',
          headingBorder: 'border-rose-200',
          linkColor: 'text-gray-600',
          linkHover: 'hover:text-gray-900',
          inputBg: 'bg-white border-rose-200 text-gray-700 placeholder-gray-400 focus:border-rose-300',
          submitBtn: 'bg-rose-400 text-white hover:bg-rose-500',
          tagBg: 'bg-white/70 hover:bg-white',
          divider: 'border-rose-200',
          addressText: 'text-gray-500',
          logoText: 'text-gray-900',
          sublogoText: 'text-gray-500',
        };
      case 'white':
        return {
          footerBg: 'bg-gradient-to-br from-gray-800 to-gray-900',
          textColor: 'text-white',
          mutedText: 'text-white/80',
          border: 'border-white/20',
          headingBorder: 'border-white/20',
          linkColor: 'text-white/80',
          linkHover: 'hover:text-white',
          inputBg: 'bg-white/10 border-white/30 text-white placeholder-white/60 focus:border-white/60',
          submitBtn: 'bg-white text-gray-800 hover:bg-white/90',
          tagBg: 'bg-white/10 hover:bg-white/20',
          divider: 'border-white/20',
          addressText: 'text-white/70',
          logoText: 'text-white',
          sublogoText: 'text-white/70',
        };
      default: // tech
        return {
          footerBg: 'bg-gradient-to-br from-teal-600 to-teal-700',
          textColor: 'text-white',
          mutedText: 'text-white/80',
          border: 'border-white/20',
          headingBorder: 'border-white/20',
          linkColor: 'text-white/80',
          linkHover: 'hover:text-white',
          inputBg: 'bg-white/10 border-white/30 text-white placeholder-white/60 focus:border-white/60',
          submitBtn: 'bg-white text-teal-600 hover:bg-white/90',
          tagBg: 'bg-white/10 hover:bg-white/20',
          divider: 'border-white/20',
          addressText: 'text-white/80',
          logoText: 'text-white',
          sublogoText: 'text-white/80',
        };
    }
  })();

  return (
    <footer>

      {/* ════ 手機版：折疊 Footer ════ */}
      <div className={`lg:hidden ${mobileFooterTheme.footerBg} ${mobileFooterTheme.textColor}`}>

        {/* 頂部 Bar（常駐） */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${mobileFooterTheme.border}`}>
          <div className="flex items-center gap-2">
            <img
              src="https://public.readdy.ai/ai/img_res/d72a3498-c157-4cc6-8081-f37fa1e12754.png"
              alt="藍舍AI地產"
              className="h-6 w-6 object-contain"
            />
            <span className="text-xs font-bold">藍舍AI地產</span>
            <span className={`${mobileFooterTheme.mutedText} text-xs opacity-60`}>© {currentYear}</span>
          </div>
          <button
            type="button"
            onClick={() => setMobileExpanded((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 ${mobileFooterTheme.menuBtn} rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap`}
          >
            <i className={`text-sm transition-transform duration-200 ${mobileExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
            {mobileExpanded ? '收起' : '選單'}
          </button>
        </div>

        {/* 展開內容區（bar 下方） */}
        {mobileExpanded && (
          <div className={`px-4 pt-5 pb-6 border-t ${mobileFooterTheme.border}`}>

            {/* 連結四欄 → 兩欄 grid */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-6 mb-6">
              <div>
                <h4 className={`text-xs font-bold uppercase tracking-wider ${mobileFooterTheme.sectionTitle} mb-2.5`}>租房服務</h4>
                <ul className={`space-y-2 text-xs ${mobileFooterTheme.mutedText}`}>
                  <li><Link to="/regions" className="flex items-center gap-1.5 hover:opacity-100 opacity-80"><i className="ri-search-line text-xs"></i>找房源</Link></li>
                  <li><Link to="/publish" className="flex items-center gap-1.5 hover:opacity-100 opacity-80"><i className="ri-home-4-line text-xs"></i>發布房源</Link></li>
                  <li><Link to="/no-commission" className="flex items-center gap-1.5 hover:opacity-100 opacity-80"><i className="ri-book-open-line text-xs"></i>租房指南</Link></li>
                  <li><Link to="/contact" className="flex items-center gap-1.5 hover:opacity-100 opacity-80"><i className="ri-question-line text-xs"></i>常見問題</Link></li>
                </ul>
              </div>
              <div>
                <h4 className={`text-xs font-bold uppercase tracking-wider ${mobileFooterTheme.sectionTitle} mb-2.5`}>法律 &amp; 關於</h4>
                <ul className={`space-y-2 text-xs ${mobileFooterTheme.mutedText}`}>
                  <li><Link to="/terms" className="flex items-center gap-1.5 hover:opacity-100 opacity-80"><i className="ri-file-text-line text-xs"></i>用戶協議</Link></li>
                  <li><Link to="/privacy-policy" className="flex items-center gap-1.5 hover:opacity-100 opacity-80"><i className="ri-shield-line text-xs"></i>隱私政策</Link></li>
                  <li><Link to="/contact" className="flex items-center gap-1.5 hover:opacity-100 opacity-80"><i className="ri-mail-line text-xs"></i>聯絡我們</Link></li>
                  <li><Link to="/ai-features" className="flex items-center gap-1.5 hover:opacity-100 opacity-80"><i className="ri-robot-2-line text-xs"></i>AI功能有哪些</Link></li>
                </ul>
              </div>
            </div>

            {/* 訂閱表單（簡版） */}
            <form
              id="newsletter-form-mobile"
              data-readdy-form
              className="flex gap-2 mb-5"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target as HTMLFormElement;
                const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
                try {
                  await fetch('https://readdy.ai/api/form/d6p7msma739gopmlgmi0', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: data.toString(),
                  });
                  form.reset();
                } catch (_) { /* silent */ }
              }}
            >
              <input
                type="email"
                name="email"
                placeholder="訂閱最新房源資訊"
                className={`flex-1 min-w-0 px-3 py-2 rounded-lg border ${mobileFooterTheme.inputBg} ${mobileFooterTheme.textColor} placeholder-current placeholder-opacity-50 focus:outline-none text-xs`}
                required
              />
              <button
                type="submit"
                className={`px-4 py-2 ${mobileFooterTheme.submitBtn} rounded-lg font-semibold text-xs transition-colors flex-shrink-0 whitespace-nowrap cursor-pointer`}
              >
                訂閱
              </button>
            </form>

            {/* 聯絡資訊 */}
            <div className={`flex flex-col gap-1.5 text-xs ${mobileFooterTheme.mutedText}`}>
              <a href="mailto:info@ailanshe.com" className="flex items-center gap-1.5 hover:opacity-100">
                <i className="ri-mail-line"></i>info@ailanshe.com
              </a>
            </div>
          </div>
        )}
      </div>

      {/* ════ 桌機版：完整 Footer ════ */}
      <div className={`hidden lg:block ${desktopFooterTheme.footerBg} ${desktopFooterTheme.textColor}`}>
        <div className="max-w-7xl mx-auto px-6 py-16">

          {/* Top Section */}
          <div className="flex items-center justify-between gap-3 mb-12">
            <div className="flex items-center gap-2">
              <img
                src="https://public.readdy.ai/ai/img_res/d72a3498-c157-4cc6-8081-f37fa1e12754.png"
                alt="藍舍AI地產"
                className="h-10 w-10 object-contain"
              />
              <span className={`text-xl font-bold ${desktopFooterTheme.logoText}`}>藍舍AI地產</span>
            </div>
            <p className={`${desktopFooterTheme.sublogoText} text-sm`}>AI智能房地產 · 全方位服務</p>
          </div>

          {/* Middle Section */}
          <div className="grid grid-cols-4 gap-8 mb-12">
            <div>
              <h4 className={`text-lg font-semibold mb-4 border-b ${desktopFooterTheme.headingBorder} pb-2`}>租房服務</h4>
              <ul className={`space-y-2.5 text-sm ${desktopFooterTheme.linkColor}`}>
                <li><Link to="/regions" className={`${desktopFooterTheme.linkHover} transition-colors flex items-center gap-1.5`}><i className="ri-search-line text-xs"></i>找房源</Link></li>
                <li><Link to="/publish" className={`${desktopFooterTheme.linkHover} transition-colors flex items-center gap-1.5`}><i className="ri-home-4-line text-xs"></i>發布房源</Link></li>
                <li><Link to="/no-commission" className={`${desktopFooterTheme.linkHover} transition-colors flex items-center gap-1.5`}><i className="ri-book-open-line text-xs"></i>租房指南</Link></li>
                <li><Link to="/contact" className={`${desktopFooterTheme.linkHover} transition-colors flex items-center gap-1.5`}><i className="ri-question-line text-xs"></i>常見問題</Link></li>
              </ul>
            </div>
            <div>
              <h4 className={`text-lg font-semibold mb-4 border-b ${desktopFooterTheme.headingBorder} pb-2`}>關於我們</h4>
              <ul className={`space-y-2.5 text-sm ${desktopFooterTheme.linkColor}`}>
                <li><Link to="/" className={`${desktopFooterTheme.linkHover} transition-colors flex items-center gap-1.5`}><i className="ri-information-line text-xs"></i>平台介紹</Link></li>
                <li><a href="https://github.com" target="_blank" rel="noopener noreferrer nofollow" className={`${desktopFooterTheme.linkHover} transition-colors flex items-center gap-1.5`}><i className="ri-github-line text-xs"></i>開源項目</a></li>
                <li><Link to="/contact" className={`${desktopFooterTheme.linkHover} transition-colors flex items-center gap-1.5`}><i className="ri-user-add-line text-xs"></i>加入我們</Link></li>
                <li><Link to="/contact" className={`${desktopFooterTheme.linkHover} transition-colors flex items-center gap-1.5`}><i className="ri-mail-line text-xs"></i>聯絡方式</Link></li>
              </ul>
            </div>
            <div>
              <h4 className={`text-lg font-semibold mb-4 border-b ${desktopFooterTheme.headingBorder} pb-2`}>法律條款</h4>
              <ul className={`space-y-2.5 text-sm ${desktopFooterTheme.linkColor}`}>
                <li><Link to="/terms" className={`${desktopFooterTheme.linkHover} transition-colors flex items-center gap-1.5`}><i className="ri-file-text-line text-xs"></i>用戶協議</Link></li>
                <li><Link to="/privacy-policy" className={`${desktopFooterTheme.linkHover} transition-colors flex items-center gap-1.5`}><i className="ri-shield-line text-xs"></i>隱私政策</Link></li>
                <li><a href="#" className={`${desktopFooterTheme.linkHover} transition-colors flex items-center gap-1.5`}><i className="ri-alert-line text-xs"></i>免責聲明</a></li>
                <li><Link to="/contact" className={`${desktopFooterTheme.linkHover} transition-colors flex items-center gap-1.5`}><i className="ri-feedback-line text-xs"></i>投訴建議</Link></li>
              </ul>
            </div>
            <div>
              <h4 className={`text-lg font-semibold mb-4 border-b ${desktopFooterTheme.headingBorder} pb-2`}>關注我們</h4>
              <p className={`text-sm ${desktopFooterTheme.mutedText} mb-3`}>© 藍舍AI地產 {currentYear}・保留所有權利</p>
              <div className="flex flex-wrap gap-3 text-sm">
                <a href="#" className={`flex items-center gap-1 ${desktopFooterTheme.tagBg} transition-colors px-3 py-1.5 rounded-full whitespace-nowrap cursor-pointer`}><i className="ri-wechat-line"></i>微信</a>
                <a href="#" className={`flex items-center gap-1 ${desktopFooterTheme.tagBg} transition-colors px-3 py-1.5 rounded-full whitespace-nowrap cursor-pointer`}><i className="ri-weibo-line"></i>微博</a>
                <a href="https://github.com" target="_blank" rel="noopener noreferrer nofollow" className={`flex items-center gap-1 ${desktopFooterTheme.tagBg} transition-colors px-3 py-1.5 rounded-full whitespace-nowrap cursor-pointer`}><i className="ri-github-line"></i>GitHub</a>
                <a href="mailto:info@ailanshe.com" className={`flex items-center gap-1 ${desktopFooterTheme.tagBg} transition-colors px-3 py-1.5 rounded-full whitespace-nowrap cursor-pointer`}><i className="ri-mail-line"></i>郵箱</a>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className={`grid grid-cols-3 gap-8 items-center pt-8 border-t ${desktopFooterTheme.border}`}>
            <div className="w-full h-48 rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/a92e6f4e9d8cc6a5dd29bd475afe837d.jpg"
                alt="平台展示"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-center">
              <p className="text-3xl font-serif leading-relaxed">讓每一次租房<br />都是美好的開始</p>
            </div>
            <div className="text-right">
              <p className={`${desktopFooterTheme.mutedText} text-sm mb-1`}>訂閱我們的資訊</p>
              <p className={`${desktopFooterTheme.mutedText} text-sm mb-3`}>獲取最新房源信息</p>
              <form id="newsletter-form" data-readdy-form className="flex gap-2 justify-end">
                <input
                  type="email"
                  name="email"
                  placeholder="輸入您的郵箱"
                  className={`w-48 px-3 py-2 rounded-lg border ${desktopFooterTheme.inputBg} focus:outline-none text-sm`}
                  required
                />
                <button
                  type="submit"
                  className={`px-4 py-2 ${desktopFooterTheme.submitBtn} rounded-lg font-medium transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer text-sm flex-shrink-0`}
                >
                  訂閱<i className="ri-arrow-right-line"></i>
                </button>
              </form>
            </div>
          </div>

          {/* Contact */}
          <div className={`mt-6 pt-6 border-t ${desktopFooterTheme.divider}`}>
            <address className={`not-italic text-sm ${desktopFooterTheme.addressText} flex items-center gap-4`}>
              <strong className={`${desktopFooterTheme.textColor} font-semibold`}>藍舍AI地產</strong>
              <span className="opacity-30">|</span>
              <a href="tel:+81-xxx-xxx-xxxx" className={`${desktopFooterTheme.linkHover} transition-colors flex items-center gap-1`}><i className="ri-phone-line text-xs"></i>+81-xxx-xxx-xxxx</a>
              <span className="opacity-30">|</span>
              <a href="mailto:info@ailanshe.com" className={`${desktopFooterTheme.linkHover} transition-colors flex items-center gap-1`}><i className="ri-mail-line text-xs"></i>info@ailanshe.com</a>
            </address>
          </div>
        </div>
      </div>

      {/* 桌機版：newsletter submit script */}
      <script dangerouslySetInnerHTML={{
        __html: `
          document.getElementById('newsletter-form')?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const form = e.target;
            const data = new URLSearchParams(new FormData(form));
            try {
              const res = await fetch('https://readdy.ai/api/form/d6p7msma739gopmlgmi0', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: data.toString()
              });
              if (res.ok) { form.reset(); }
            } catch (_) {}
          });
        `
      }} />
    </footer>
  );
}
